/* ═══════════════════════════════════════════════════════════════
   taskDataLoader — the ONE shared task-bundle loader (Decision 24).
   Every consumer that needs tasks + assignments + project links (the
   project Tasks widget, the Attention widget's overdue-tasks part, the
   widget-builder's "tasks" source, and useTasksData) calls this instead
   of hand-rolling its own Promise.allSettled. Visibility/permission
   decisions still run entirely through taskPermissions.ts — this module
   only loads rows.
   ═══════════════════════════════════════════════════════════════ */
import type { Task, TaskAssignment, TaskProjectLink } from '../../../shared/types';
import { TaskService } from '../../services/taskService';

export interface TasksBundle {
  tasks: Task[];
  assignments: TaskAssignment[];
  links: TaskProjectLink[];
  /** true when the assignments fetch was rejected (e.g. a 403 for a
   *  viewer with tasks.view_own but no assignment-read right) — callers
   *  that otherwise filter by assignment should trust the server-scoped
   *  `tasks` list instead of treating an empty array as "no assignments". */
  assignmentsScoped: boolean;
}

/** `assignments` and `links` are child tables — a rejection on either is
 *  tolerated and settles to an empty list (flagged via `assignmentsScoped`
 *  for the assignments case). A rejected TASKS fetch is the primary table
 *  and is NOT swallowed: it rethrows so a caller's existing retry/error
 *  handling still fires exactly as before this loader existed. */
export async function loadTasksBundle(): Promise<TasksBundle> {
  const [t, a, l] = await Promise.allSettled([
    TaskService.listTasks(),
    TaskService.listTaskAssignments(),
    TaskService.listTaskProjectLinks(),
  ]);
  if (t.status === 'rejected') throw t.reason;
  // cloudCache's list()/query() return the SAME array reference it keeps
  // internally (mutated in place on every save/remove — see cloudCache.ts
  // updateCacheEntry). Every collection handed back here is copied into a
  // fresh array so a React setState from it is never a same-reference
  // no-op (the live-update bug this loader exists to prevent).
  return {
    tasks: [...t.value],
    assignments: a.status === 'fulfilled' ? [...a.value] : [],
    assignmentsScoped: a.status !== 'fulfilled',
    links: l.status === 'fulfilled' ? [...l.value] : [],
  };
}
